// Thin Storyblok blok wrappers for the Findex Connect page (FAMS-303).
// The ported components keep their original prop shape (data + sectionId);
// these wrappers adapt them to the blok registry and add storyblokEditable so
// the visual editor can select, live-update, and reorder body sections.
import { SbBlokData, storyblokEditable } from '@storyblok/react/rsc'
import { StoryblokServerComponent } from '@storyblok/react/rsc'

import { BackgroundSwitcher } from '@/components/findex-connect/BackgroundSwitcher/BackgroundSwitcher'
import { FindexDifferenceBlock } from '@/components/findex-connect/FindexDifferenceBlock/FindexDifferenceBlock'
import { Footer } from '@/components/findex-connect/Footer/Footer'
import { GetStartedBlock } from '@/components/findex-connect/GetStartedBlock/GetStartedBlock'
import { HeroBlock } from '@/components/findex-connect/HeroBlock/HeroBlock'
import { HowItWorksBlock } from '@/components/findex-connect/HowItWorksBlock/HowItWorksBlock'
import { Marquee } from '@/components/findex-connect/Marquee/Marquee'
import { ServicesBlock } from '@/components/findex-connect/ServicesBlock/ServicesBlock'
import { StatsBlock } from '@/components/findex-connect/StatsBlock/StatsBlock'
import { StickyNav } from '@/components/findex-connect/StickyNav/StickyNav'
import { TestimonialBlock } from '@/components/findex-connect/TestimonialBlock/TestimonialBlock'
import {
  FindexDifferenceBlockSb,
  FooterSb,
  GetStartedBlockSb,
  HeroBlockSb,
  HowItWorksBlockSb,
  LogoMarqueeSb,
  PageSb,
  ServicesBlockSb,
  StatsBlockSb,
  TestimonialBlockSb,
} from '@/components/findex-connect/types/component-types-sb'

const editable = (blok: unknown) => storyblokEditable(blok as SbBlokData)

// Content type: replicates the fixed page structure of the standalone app.
// The hero always renders above BackgroundSwitcher; the remaining body bloks
// render inside it in body order, so editors can reorder sections.
export function ConnectPageBlok({ blok }: { blok: PageSb & { _editable?: string } }) {
  const body = (blok.body || []) as unknown as SbBlokData[]
  const hero = body.filter((b) => b.component === 'HeroBlock')
  const rest = body.filter((b) => b.component !== 'HeroBlock')

  return (
    <div {...editable(blok)} className="OUTER relative overflow-x-hidden md:overflow-x-auto">
      <StickyNav sections={['overview', 'how-it-works', 'services', 'why-findex', 'reviews']} />
      {hero.map((b) => (
        <StoryblokServerComponent blok={b} key={b._uid} />
      ))}
      <BackgroundSwitcher>
        <div className="MAIN-CONTAINER flex flex-col">
          {rest.map((b) => (
            <StoryblokServerComponent blok={b} key={b._uid} />
          ))}
        </div>
      </BackgroundSwitcher>
    </div>
  )
}

export function HeroBlockBlok({ blok }: { blok: HeroBlockSb }) {
  return (
    <div {...editable(blok)}>
      <HeroBlock sectionId="overview" data={blok} />
    </div>
  )
}

export function HowItWorksBlockBlok({ blok }: { blok: HowItWorksBlockSb }) {
  return (
    <div {...editable(blok)}>
      <HowItWorksBlock sectionId="how-it-works" data={blok} />
    </div>
  )
}

export function LogoMarqueeBlok({ blok }: { blok: LogoMarqueeSb }) {
  return (
    <div {...editable(blok)} className="MARQUEE-BLOCK w-full max-w-[100lvw]">
      <Marquee title="Trusted by industry leaders" items={blok.logo} />
    </div>
  )
}

export function ServicesBlockBlok({ blok }: { blok: ServicesBlockSb }) {
  return (
    <div {...editable(blok)}>
      <ServicesBlock sectionId="services" data={blok} />
    </div>
  )
}

export function FindexDifferenceBlockBlok({ blok }: { blok: FindexDifferenceBlockSb }) {
  return (
    <div {...editable(blok)}>
      <FindexDifferenceBlock sectionId="why-findex" data={blok} />
    </div>
  )
}

export function StatsBlockBlok({ blok }: { blok: StatsBlockSb }) {
  return (
    <div {...editable(blok)}>
      <StatsBlock data={blok} />
    </div>
  )
}

export function TestimonialBlockBlok({ blok }: { blok: TestimonialBlockSb }) {
  return (
    <div {...editable(blok)}>
      <TestimonialBlock sectionId="reviews" data={blok} />
    </div>
  )
}

export function GetStartedBlockBlok({ blok }: { blok: GetStartedBlockSb }) {
  return (
    <div {...editable(blok)}>
      <GetStartedBlock data={blok} />
    </div>
  )
}

export function FooterBlok({ blok }: { blok: FooterSb }) {
  return (
    <div {...editable(blok)}>
      <Footer data={blok} />
    </div>
  )
}
